/** @jsxImportSource @emotion/react */

import { useQuery } from '@apollo/client'
import Typography from '@mui/material/Typography'
import Button from '@mui/material/Button'
import AddRoundedIcon from '@mui/icons-material/AddRounded'
import IconButton from '@mui/material/IconButton'
import { Link, useSearchParams } from 'react-router-dom'
import { ReactComponent as EditIcon } from 'assets/icons/edit.svg'
import { Loader } from 'components/Loader'
import { FIND_ALL_SUCCESS_STORY_SECTIONS } from 'queries/successStory'
import { TAKE_ALL_ITEMS_COUNT } from 'constants/consts'
import * as tableButtonsClasses from 'components/TableButtons/styles'
import { cmsRoutes, useLink } from 'routes/cmsRoutes'
import TabsNavigation from 'components/TabsNavigation'
import { TabsItem } from 'types/tabsItem'
import {
  findAllSuccessStorySection,
  findAllSuccessStorySectionVariables,
} from '__generatedTypes__/findAllSuccessStorySection'
import withPlatformCheck from 'HOCs/withPlatformCheck'
import { findAllPlatforms_findAllPlatforms_items as Platform } from '__generatedTypes__/findAllPlatforms'
import { AllSuccessStoriesContent } from '../AllSuccessStories'
import { PAGE_QUERY_PARAM } from '../BlogPostList'
import { SUCCESS_STORIES_SUB_TAB_QUERY_PARAM, SUCCESS_STORIES_SUB_TABS, SUCCESS_STORY_SECTIONS_TAB } from '../consts'
import * as blogClasses from '../styles'
import * as classes from './styles'
import { SectionItem } from './SectionItem'

type Props = {
  platform: Platform
}

const SuccessStorySections = ({ platform }: Props) => {
  const { getLink } = useLink()

  const { data, loading } = useQuery<findAllSuccessStorySection, findAllSuccessStorySectionVariables>(
    FIND_ALL_SUCCESS_STORY_SECTIONS,
    {
      fetchPolicy: 'network-only',
      variables: {
        query: {
          take: TAKE_ALL_ITEMS_COUNT,
          where: {
            platform: {
              id: platform.id,
            },
          },
        },
      },
    }
  )

  const successSections = data?.findAllSuccessStorySection?.items || []

  if (loading) {
    return <Loader />
  }

  return (
    <div>
      {successSections.length ? (
        <ul css={classes.list}>
          {successSections.map((section) => {
            const sectionItems = section.successStory.map(({ id, fullName }) => ({ id, label: fullName }))
            return (
              <li key={section.id} css={classes.listItem}>
                <div>
                  <Typography
                    variant="subtitle2"
                    css={classes.title}
                    data-testid={`success-stories-section-${section.id}`}
                  >
                    {section.title}
                  </Typography>
                  <SectionItem orderIds={section.successStoryIds} items={sectionItems} />
                </div>
                <Link to={getLink(cmsRoutes.ORGANIZE_EDIT_SUCCESS_STORIES, { id: section.id })}>
                  <IconButton css={classes.editButton} LinkComponent={Link} data-testid="edit-button">
                    <EditIcon />
                  </IconButton>
                </Link>
              </li>
            )
          })}
        </ul>
      ) : (
        <Typography variant="h6">No sections found</Typography>
      )}

      <Link css={classes.addLink} to={getLink(cmsRoutes.ORGANIZE_CREATE_SUCCESS_STORIES)}>
        <Button css={classes.addButton} variant="outlined" color="blue" size="large" fullWidth>
          Add new section in Success Stories <AddRoundedIcon css={tableButtonsClasses.iconPlus} />
        </Button>
      </Link>
    </div>
  )
}

export const SuccessStoriesContent = ({ platform }: Props) => {
  const [searchParams, setSearchParams] = useSearchParams()
  const activeSubTab =
    SUCCESS_STORIES_SUB_TABS.find(({ id }) => id === searchParams.get(SUCCESS_STORIES_SUB_TAB_QUERY_PARAM)) ??
    SUCCESS_STORY_SECTIONS_TAB

  const handleChangeSubTab = (tab: TabsItem) => {
    setSearchParams(
      (currentParams) => {
        const nextParams = new URLSearchParams(currentParams)

        tab.id === SUCCESS_STORY_SECTIONS_TAB.id
          ? nextParams.delete(SUCCESS_STORIES_SUB_TAB_QUERY_PARAM)
          : nextParams.set(SUCCESS_STORIES_SUB_TAB_QUERY_PARAM, tab.id)

        nextParams.delete(PAGE_QUERY_PARAM)

        return nextParams
      },
      { replace: true }
    )
  }

  return (
    <div>
      <TabsNavigation
        css={blogClasses.subTabs}
        tabs={SUCCESS_STORIES_SUB_TABS}
        value={activeSubTab}
        onChange={handleChangeSubTab}
      />

      {activeSubTab.id === SUCCESS_STORY_SECTIONS_TAB.id ? (
        <SuccessStorySections platform={platform} />
      ) : (
        <AllSuccessStoriesContent platform={platform} />
      )}
    </div>
  )
}

export const SuccessStories = withPlatformCheck(SuccessStoriesContent)
